import { useState, useEffect, useCallback } from 'react';
import { StorageService } from '../services/storage';

/**
 * Page visibility preferences
 * Controls which optional pages are shown in the sidebar
 * Independent of simple/expert mode
 */
export interface PageVisibilityPrefs {
  cryptocom: boolean;
  kucoin: boolean;
}

const DEFAULT_PREFS: PageVisibilityPrefs = {
  cryptocom: false, // Hidden by default
  kucoin: false,    // Hidden by default
};

const STORAGE_KEY = 'page_visibility_prefs';

// Custom event for page visibility changes (to sync across components)
const PAGE_VISIBILITY_CHANGE_EVENT = 'page-visibility-change';

/**
 * Hook to manage page visibility preferences
 * These are independent of the simple/expert mode
 * Uses custom events to sync state across all components
 */
export function usePageVisibility(): [PageVisibilityPrefs, (page: keyof PageVisibilityPrefs, visible: boolean) => void] {
  const [prefs, setPrefs] = useState<PageVisibilityPrefs>(() => {
    const saved = StorageService.get<PageVisibilityPrefs>(STORAGE_KEY, DEFAULT_PREFS);
    return { ...DEFAULT_PREFS, ...saved };
  });

  // Listen for visibility changes from other components
  useEffect(() => {
    const handleVisibilityChange = (e: CustomEvent<PageVisibilityPrefs>) => {
      setPrefs(e.detail);
    };

    window.addEventListener(PAGE_VISIBILITY_CHANGE_EVENT, handleVisibilityChange as EventListener);
    return () => {
      window.removeEventListener(PAGE_VISIBILITY_CHANGE_EVENT, handleVisibilityChange as EventListener);
    };
  }, []);

  const setPageVisibility = useCallback((page: keyof PageVisibilityPrefs, visible: boolean) => {
    // Use functional update to avoid stale closure issues
    setPrefs(prevPrefs => {
      const newPrefs = { ...prevPrefs, [page]: visible };

      // Persist to localStorage
      StorageService.set(STORAGE_KEY, newPrefs);

      console.log(`📄 Page visibility changed: ${page} = ${visible}`, newPrefs);

      // Dispatch event to sync across all components (async to allow state to settle)
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent(PAGE_VISIBILITY_CHANGE_EVENT, { detail: newPrefs }));
      }, 0);

      return newPrefs;
    });
  }, []); // No dependencies needed - using functional update

  return [prefs, setPageVisibility];
}

/**
 * Get page visibility preferences (non-hook version for use outside React components)
 */
export function getPageVisibilityPrefs(): PageVisibilityPrefs {
  return StorageService.get<PageVisibilityPrefs>(STORAGE_KEY, DEFAULT_PREFS);
}
